import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { storiesApi, authApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import BlockRenderer from '../components/BlockRenderer';
import SEO, { SITE_URL } from '../components/SEO';

const TYPE_COLORS = {
  personal: { bg: 'var(--green-light)', color: 'var(--green)' },
  investigation: { bg: 'var(--red-light)', color: 'var(--red)' },
  talent: { bg: 'var(--amber-light)', color: 'var(--amber)' },
  welfare: { bg: 'var(--blue-light)', color: 'var(--blue)' },
  interview: { bg: 'var(--purple-light)', color: 'var(--purple)' },
};

function AuthorCard({ author, currentUser }) {
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(author.follower_count || 0);

  const handleFollow = async () => {
    try {
      const res = await authApi.followToggle(author.username);
      setFollowing(res.data.following);
      setFollowerCount(res.data.follower_count);
    } catch { /* ignore */ }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '1rem',
      padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)',
      border: '1px solid var(--border)',
    }}>
      <Link to={`/profile/${author.username}`}>
        {author.avatar
          ? <img src={author.avatar} alt={author.username} style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : (
            <div style={{
              width: 52, height: 52, borderRadius: '50%', background: 'var(--green)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0,
            }}>
              {(author.first_name?.[0] || author.username[0]).toUpperCase()}
            </div>
          )
        }
      </Link>
      <div style={{ flex: 1 }}>
        <Link to={`/profile/${author.username}`} style={{ fontWeight: 700, color: 'var(--text)' }}>
          {author.first_name ? `${author.first_name} ${author.last_name}` : author.username}
        </Link>
        <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.78rem', color: 'var(--text-light)', marginTop: 2 }}>
          @{author.username} · {followerCount} followers · {author.story_count || 0} stories
        </div>
        {author.bio && (
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.4rem', lineHeight: 1.6 }}>
            {author.bio.slice(0, 160)}{author.bio.length > 160 ? '…' : ''}
          </p>
        )}
      </div>
      {currentUser && currentUser.username !== author.username && (
        <button
          onClick={handleFollow}
          className={`btn btn-sm ${following ? 'btn-ghost' : 'btn-outline'}`}
          style={{ flexShrink: 0 }}
        >
          {following ? 'Following' : 'Follow'}
        </button>
      )}
    </div>
  );
}

export default function StoryDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setStory(null);
    storiesApi.detail(slug).then(r => {
      setStory(r.data);
      setLiked(r.data.is_liked);
      setLikeCount(r.data.like_count);
      setBookmarked(r.data.is_bookmarked);
    }).catch(() => navigate('/stories'));
  }, [slug, navigate]);

  const submitComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await storiesApi.comment(slug, { content: comment });
      const r = await storiesApi.detail(slug);
      setStory(r.data);
      setComment('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async () => {
    if (!user) return navigate('/login');
    try {
      const res = await storiesApi.like(slug);
      setLiked(res.data.liked);
      setLikeCount(res.data.like_count);
    } catch { /* ignore */ }
  };

  const handleBookmark = async () => {
    if (!user) return navigate('/login');
    try {
      const res = await storiesApi.bookmark(slug);
      setBookmarked(res.data.bookmarked);
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await storiesApi.delete(slug);
      navigate('/stories');
    } finally {
      setDeleting(false);
    }
  };

  if (!story) return <div className="loading-text">Loading story…</div>;

  const authorName = story.author.first_name
    ? `${story.author.first_name} ${story.author.last_name}`.trim()
    : story.author.username;

  const storyJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: story.title,
    description: story.excerpt,
    image: story.cover_image_url || `${SITE_URL}/og-default.png`,
    datePublished: story.created_at,
    dateModified: story.updated_at,
    author: {
      '@type': 'Person',
      name: authorName,
      url: `${SITE_URL}/profile/${story.author.username}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pitch & Pen',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/stories/${story.slug}` },
    articleSection: story.story_type,
    keywords: story.tags?.map(t => t.name).join(', '),
    wordCount: story.content?.split(/\s+/).length || 0,
    timeRequired: `PT${story.read_time || 1}M`,
  };

  const tc = TYPE_COLORS[story.story_type] || TYPE_COLORS.personal;
  const isAuthor = user && user.username === story.author.username;
  const dateStr = new Date(story.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <article>
      <SEO
        title={story.title}
        description={story.excerpt}
        image={story.cover_image_url}
        url={`${SITE_URL}/stories/${story.slug}`}
        type="article"
        article={{
          author: authorName,
          publishedTime: story.created_at,
          modifiedTime: story.updated_at,
          section: story.story_type,
          tags: story.tags?.map(t => t.name),
        }}
        jsonLd={storyJsonLd}
      />
      {/* Cover */}
      {story.cover_image_url && (
        <div style={{ width: '100%', maxHeight: 480, overflow: 'hidden', marginBottom: 0 }}>
          <img src={story.cover_image_url} alt={story.title}
            style={{ width: '100%', maxHeight: 480, objectFit: 'cover', display: 'block' }} />
        </div>
      )}

      <div style={{ padding: '3rem 0 5rem' }}>
        <div className="container-narrow">
          {/* Type badge + edit actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ ...tc, padding: '4px 12px', borderRadius: 20, fontSize: '0.75rem', fontFamily: 'var(--font-sans)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {story.story_type.replace('_', ' ')}
            </span>
            {isAuthor && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Link to={`/write?edit=${story.slug}`} className="btn btn-ghost btn-sm">Edit</Link>
                <button onClick={handleDelete} disabled={deleting} className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }}>
                  {deleting ? '…' : 'Delete'}
                </button>
              </div>
            )}
          </div>

          {/* Title */}
          <h1 style={{ fontSize: 'clamp(1.8rem, 5vw, 2.8rem)', lineHeight: 1.15, marginBottom: '1.1rem' }}>
            {story.title}
          </h1>

          {/* Excerpt */}
          <p style={{ fontSize: '1.15rem', fontStyle: 'italic', color: 'var(--text-muted)', lineHeight: 1.65, marginBottom: '1.5rem' }}>
            {story.excerpt}
          </p>

          {/* Meta row */}
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem 1rem',
            fontFamily: 'var(--font-sans)', fontSize: '0.85rem', color: 'var(--text-light)',
            paddingBottom: '1.75rem', marginBottom: '2rem', borderBottom: '1px solid var(--border)',
          }}>
            <Link to={`/profile/${story.author.username}`} style={{ color: 'var(--text)', fontWeight: 600 }}>
              {story.author.first_name ? `${story.author.first_name} ${story.author.last_name}` : story.author.username}
            </Link>
            <span>·</span>
            <span>{dateStr}</span>
            <span>·</span>
            <span>{story.read_time} min read</span>
            <span>·</span>
            <span>{story.views} views</span>
            {story.tags?.length > 0 && (
              <>
                <span>·</span>
                <div className="tags-row">
                  {story.tags.map(t => <span key={t.id} className="tag-chip">{t.name}</span>)}
                </div>
              </>
            )}
          </div>

          {/* Body */}
          <BlockRenderer blocks={story.blocks} legacyContent={story.content} />

          {/* Like / Bookmark actions */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--border)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={handleLike} className={`action-btn ${liked ? 'liked' : ''}`}>
              {liked ? '❤️' : '🤍'} {likeCount} {likeCount === 1 ? 'like' : 'likes'}
            </button>
            <button onClick={handleBookmark} className={`action-btn ${bookmarked ? 'bookmarked' : ''}`}>
              {bookmarked ? '🔖 Saved' : '🔖 Save'}
            </button>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => navigator.share?.({ title: story.title, url: window.location.href }) || navigator.clipboard?.writeText(window.location.href)}
              className="action-btn"
            >
              🔗 Share
            </button>
          </div>

          {/* Author card */}
          <div style={{ marginTop: '2.5rem' }}>
            <h4 style={{ fontFamily: 'var(--font-sans)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-light)', marginBottom: '0.75rem' }}>
              About the author
            </h4>
            <AuthorCard author={story.author} currentUser={user} />
          </div>

          {/* Comments */}
          <div style={{ marginTop: '3rem' }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.2rem' }}>
              Comments ({story.comments.length})
            </h3>
            {story.comments.length === 0 && (
              <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                No comments yet. Be the first to share your thoughts.
              </p>
            )}
            {story.comments.map(c => (
              <div key={c.id} style={{
                padding: '1.1rem 1.25rem', background: 'var(--bg)', borderRadius: 'var(--radius)',
                marginBottom: '0.75rem', border: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: 'var(--green)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {(c.author.first_name?.[0] || c.author.username[0]).toUpperCase()}
                  </div>
                  <div>
                    <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: '0.875rem' }}>
                      {c.author.first_name ? `${c.author.first_name} ${c.author.last_name}` : c.author.username}
                    </span>
                    <span style={{ fontFamily: 'var(--font-sans)', fontSize: '0.75rem', color: 'var(--text-light)', marginLeft: '0.5rem' }}>
                      {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                </div>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.65 }}>{c.content}</p>
              </div>
            ))}

            {user ? (
              <div style={{ marginTop: '1.5rem' }}>
                <textarea
                  value={comment} onChange={e => setComment(e.target.value)} rows={3}
                  placeholder="Share your thoughts…"
                  className="form-textarea"
                />
                <button onClick={submitComment} disabled={submitting || !comment.trim()} className="btn btn-primary" style={{ marginTop: '0.75rem' }}>
                  {submitting ? 'Posting…' : 'Post comment'}
                </button>
              </div>
            ) : (
              <p style={{ fontFamily: 'var(--font-sans)', fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '1rem', padding: '1rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                <Link to="/login" style={{ color: 'var(--green)', fontWeight: 600 }}>Sign in</Link> to leave a comment.
              </p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
